/** Motivation factors. Keyed by API id. */
export const order = [15, 2, 3, 6, 7, 1, 12, 13, 16, 5, 14, 11, 17, 9, 10, 8, 4, 0];

const dictionary = {
  "en": {
    "15": {
      "id": 15,
      "slug": "lackOfStress",
      "title": "Work balance",
      "lowDescription": "Considers it irrelevant that tasks are stress-free. Invests a lot of time and energy into work.",
      "highDescription": "Considers overload and tension as counter-productive. Needs a good work-life balance.",
      "summary": "Would prefer that the activity is not perceived as excessive stress; considers it important that the work is able to be accomplished in the available time, and that excessive demands do not crop up",
      "high": [
        "'Considers overload and tension as counter-productive; needs a good work-life balance.'"
      ],
      "low": [
        "Considers it is more irrelevant that tasks are stress-free; invests a lot of time and energy into work"
      ],
      "howToManage": [
        "Ask: 'What does a good work-life balance mean for you?'",
        "This person prefers to get things done on time so realistic deadlines are important.",
        "Might prefer to focus on & finish one thing at a time before moving to the next task.",
        "Huge number of tasks and challenging deadlines might stress this person a lot.",
        "Clearly separating life and work life in time use helps this person to stay motivated."
      ],
      "watchOut": []
    },
    "2": {
      "id": 2,
      "slug": "financialReward",
      "title": "Financial reward",
      "lowDescription": "Prefers a non-performance-oriented incentive system. Is not really motivated by money.",
      "highDescription": "Prefers a performance-oriented incentive system. Is motivated by financial reward.",
      "summary": "Prefers a performance-oriented or adequate pay; considers it important that extra work is rewarded\nfinancially; is motivated by  financial incentives",
      "high": [
        "Prefers a performance-oriented incentive system and is motivated by financial reward"
      ],
      "low": [
        "Prefers a non-performance-oriented incentive system and is not really motivated by money"
      ],
      "howToManage": [
        "Ask: 'What kind of financial rewards are motivating for you?'",
        "Provide opportunities to performance orientated pay or bonuses",
        "Reward extra work financially or materially",
        "Future earning possibilities through career development or growth might also motivate this person"
      ],
      "watchOut": [
        "'Money is a hygiene factor…'"
      ]
    },
    "3": {
      "id": 3,
      "slug": "security",
      "title": "Job security",
      "lowDescription": "Considers job security quite unimportant. Is prepared to take occupational risks.",
      "highDescription": "Wants a work environment that provides security and confidence for the future.",
      "summary": "Considers the security of the workplace important; appreciates a working atmosphere that offers a good degree of confidence and insouciance regarding the own future",
      "high": [
        "Wants a work environment that provides security and confidence for the future"
      ],
      "low": [
        "Considers job security as less important; is prepared to take more occupational risks."
      ],
      "howToManage": [
        "Ask: 'What safety in job life means for you?'",
        "'Safety first.'; take care that working environment is safe and career risks are moderate or lower",
        "Build confidence for a safe and stable future. Highlight stable elements that are true.",
        "If the company’s / team’s future is highly ambiguous, unclear, or risky, motivation goes down."
      ],
      "watchOut": [
        "'There are no such sure & futureproof jobs nowadays…'"
      ]
    },
    "6": {
      "id": 6,
      "slug": "harmonia",
      "title": "Harmony",
      "lowDescription": "Thinks it is important to tackle conflicts openly. Does not place high value on team harmony.",
      "highDescription": "Appreciates harmony and a feeling of unity in the team. Strives to avoid conflicts.",
      "summary": "Appreciates a harmonious working atmosphere; would like to avoid quarrels and arguments among one another; considers a feeling of solidarity as important",
      "high": [
        "Appreciates harmony and a feeling of unity in a team; strives to avoid conflicts."
      ],
      "low": [
        "Thinks it's important to tackle conflicts openly; does not place high value on team harmony"
      ],
      "howToManage": [
        "Ask: 'What harmonious working environment means for you?'",
        "This person values harmonious relationships and does not like to 'rock the boat'; focus especially on common grounds and things that people agree and share.",
        "This person is motivated by situations that require a more diplomatic approach.",
        "Avoid conflicts with this person as much as possible. When the feeling of unity goes low in a team for a long time, this person starts to feel unmotivated."
      ],
      "watchOut": []
    },
    "7": {
      "id": 7,
      "slug": "transparency",
      "title": "Honesty",
      "lowDescription": "Accepts that pieces of information are restricted to certain people. Takes rumours into account.",
      "highDescription": "Considers transparency and open communication crucial. Always wants to be honest and overt.",
      "summary": "Considers it important that people communicate openly with each  other, and therefore it is assured that everyone receives all relevant  information; does not like the fact that rumours are able to emerge",
      "high": [
        "Considers transparency and open communication crucial; always wants to be honest and overt."
      ],
      "low": [
        "Accepts that pieces of information are restricted to certain people; takes rumours into account."
      ],
      "howToManage": [
        "Ask: 'What does open communication mean for you?'",
        "Keep communication as open as possible.",
        "Cut off the rumours from the start and quickly.",
        "Create systems that support open communication in a team."
      ],
      "watchOut": []
    },
    "1": {
      "id": 1,
      "slug": "performanceRecognition",
      "title": "Recognition of performance",
      "lowDescription": "Builds on own standards. Does not feel the need for appreciation or praise.",
      "highDescription": "Strives for respect and recognition. Wants successes to be acknowledged.",
      "summary": "Would like to receive personal recognition for special achievements; considers it important that successful actions are acknowledged and also accordingly appreciated",
      "high": [
        "Strives for respect and recognition and wants successes to be acknowledged"
      ],
      "low": [
        "Builds on own standards and does not feel the need for appreciation or praise"
      ],
      "howToManage": [
        "Ask: 'What is the best recognition you have ever had in a job?'",
        "Recognize successes and give personal praise for positive outcomes and effort.",
        "Make sure that positive feedback and recognition becomes visible for others - e.g, through publication on the intranet, open communication within the team or department.",
        "If possible, give this person a limelight after he / she has done a good job."
      ],
      "watchOut": [
        "'Good job is a reward in itself.'"
      ]
    },
    "12": {
      "id": 12,
      "slug": "systematicity",
      "title": "Structure",
      "lowDescription": "Needs few structures, rules or procedures. Tolerates ambiguities well.",
      "highDescription": "Requires precisely defined processes in order to avoid any uncertainties.",
      "summary": "Considers it important that everyone always knows exactly what is to be done, and that uncertainties regarding jobs and operational sequences are strictly avoided",
      "high": [
        "Requires precisely defined processes in order to avoid any uncertainties."
      ],
      "low": [
        "Needs few structures, rules or procedures. Tolerates ambiguities well."
      ],
      "howToManage": [],
      "watchOut": []
    },
    "13": {
      "id": 13,
      "slug": "changeRate",
      "title": "Rate of change",
      "lowDescription": "Prefers well planned and gradual changes. Considers consistency and stability as important.",
      "highDescription": "Appreciates when things change at high speed. Wants improvements to be implemented quickly.",
      "summary": "Appreciates it when many things in the company change at a high rate of speed; considers it important that complicated sequences or \nantiquated approaches are very rapidly changed through appropriate measures",
      "high": [
        "Appreciates when things change at a high speed; wants improvements to be implemented quickly"
      ],
      "low": [
        "Prefers well planned and gradual changes; considers consistency and stability as important"
      ],
      "howToManage": [
        "Ask: 'What does a higher rate of change mean to you?'",
        "This person prefers a higher rate of speed in changes. Provide opportunities to implement these changes whenever possible.",
        "This person values that old procedures & solutions are quickly replaced by new ones.",
        "Adaptation to changes and new requirements in the environment is important to this person.",
        "Ask: 'What things do you think are lagging behind in our organization?'"
      ],
      "watchOut": []
    },
    "16": {
      "id": 16,
      "slug": "opportunitiesForInfluence",
      "title": "Influence possibilities",
      "lowDescription": "Prefers to work with clear directions. Is indifferent about having autonomy or power at work.",
      "highDescription": "Works independently and with self-initiative. Likes to have creative leeway and influence.",
      "summary": "Appreciates it when self-initiative is welcomed, and the willingness to self-made decisions is fostered; would not like to always have to ask \nfor permission for everything",
      "high": [
        "Works independently and with self-initiative; likes to have creative leeway and influence."
      ],
      "low": [
        "Prefers to work with clear directions; is indifferent about having autonomy or power at work"
      ],
      "howToManage": [
        "Ask: 'What does self-initiative and autonomy mean to you?'",
        "This person is motivated to work more independently towards a clear goal, with boundary conditions (e.g. budget, schedule, quality criteria) defined with a single checkpoint before the finish line, if possible.",
        "This person is motivated by encouraging self-initiative and being able to work according to one's own judgement.",
        "This person’s motivation decreases when there is a lot of constant follow-ups & reporting, overly precise instructions, somebody is constantly watching over, there is a need to repeatedly ask for permissions etc.",
        "BEWARE:",
        "'Follow-up is good, control is best. Here are the instructions, follow them closely and keep the reporting up to date at all times.'"
      ],
      "watchOut": []
    },
    "5": {
      "id": 5,
      "slug": "identification",
      "title": "Identification",
      "lowDescription": "Does not really question the sense of own job. Considers work only a necessary part of life.",
      "highDescription": "Needs the feeling of doing something meaningful at work. Wants to be proud of the work itself.",
      "summary": "Would like to be able to be enthusiastic about the job and have the feeling to do something purposeful; wants to be proud of own work, and therefore always tries to give the absolute best",
      "high": [
        "Needs the feeling of doing something meaningful at work; wants to be proud of the work itself."
      ],
      "low": [
        "Does not really question the sense of own job; considers work to be more a means to an end"
      ],
      "howToManage": [
        "Ask: 'What are you really interested in, excited or even passionate about in your job?'",
        "Provide opportunities to work on especially in these areas when possible.",
        "Show how the job is meaningful in the big picture and has a more lasting impact.",
        "This person might lose motivation quickly when there is a mismatch of persons and organizations values."
      ],
      "watchOut": [
        "'This is just a job, not so meaningful…'"
      ]
    },
    "14": {
      "id": 14,
      "slug": "developmentOpportunities",
      "title": "Development opportunities",
      "lowDescription": "Does not feel the company alone is responsible for development of skills.",
      "highDescription": "Values when personal and career development is actively promoted in a company.",
      "summary": "Considers it important that personal and career development is given  enough \"latitude\", and would like to have sufficient opportunities to improve their professional skills",
      "high": [
        "Values when personal and career development is actively promoted in a company"
      ],
      "low": [
        "Does not feel the company alone is responsible for development of skills"
      ],
      "howToManage": [
        "Ask: 'What do you want to learn? What are your TOP3 learning goals?'",
        "Provide opportunities for learning, growing and professional development.",
        "Aim to connect this person’s learning goals to the team's performance goals."
      ],
      "watchOut": [
        "Too many tasks, projects and responsibilities that do not provide opportunities to learn & grow."
      ]
    },
    "11": {
      "id": 11,
      "slug": "hierarchy",
      "title": "Hierarchy",
      "lowDescription": "Prefers plain hierarchical structures. Considers authorities quite unimportant.",
      "highDescription": "Believes in distinct management structures. Appreciates clear authorities in a company.",
      "summary": "Prefers a clear, hierarchical arrangement of responsibilities in the company; considers it important that there are authorities in the company who clearly determine the approach",
      "high": [
        "Believes in distinct management structures and appreciates clear authorities in a company."
      ],
      "low": [
        "Prefers plain hierarchical structures; considers authorities as less important"
      ],
      "howToManage": [
        "Ask: 'What do clear responsibilities mean for you?'",
        "Make responsibility areas clear: who is responsible for what?",
        "Make it clear who is responsible for certain decisions."
      ],
      "watchOut": []
    },
    "17": {
      "id": 17,
      "slug": "workingEnvironment",
      "title": "Working environment",
      "lowDescription": "Considers the functionality of workplaces or the design of office space as negligible.",
      "highDescription": "Places value on attractive and functional office space and workplaces.",
      "summary": "Prefers well-arranged rooms and workstations; appreciates workstations which are pleasant and functional",
      "high": [
        "Places value on attractive and functional office space and workplaces"
      ],
      "low": [
        "Considers the functionality of workplaces or the design of office space as negligible"
      ],
      "howToManage": [
        "Ask: 'What does a functional and attractive workplace mean to you?'",
        "Provide a pleasant working environment as possible; for different people this might mean aesthetics, functionality, cleanliness, or safety. Find out what this person values most and make relevant changes whenever possible.",
        "If possible, give opportunities to make the workstation more suitable for this person; different decorations, arrangements and location might be motivating for this person."
      ],
      "watchOut": []
    },
    "9": {
      "id": 9,
      "slug": "unity",
      "title": "Integrity",
      "lowDescription": "Considers ethics and moral in a working context irrelevant. Does not feel the need to stand behind goals.",
      "highDescription": "Considers moral principles important and wants to be able to work with clear conscience.",
      "summary": "Considers it important that objectives are pursued, whereby one can stand behind them with conviction, and clear ethical principles are adhered to; appreciates a trusting relationship with one another",
      "high": [
        "Considers moral principles as important and wants to be able to work with a clear conscience."
      ],
      "low": [
        "Considers ethics and morals in a working context as more irrelevant; does not need to stand behind goals."
      ],
      "howToManage": [
        "Ask: 'What ethical principles are particularly important to you?'",
        "This person probably has some very significant ethical values that need to be realized in everyday work.",
        "Emphasize the greatest possible transparency and honesty in your operations and decisions"
      ],
      "watchOut": [
        "'Well, this isn't exactly right, but it's not illegal.'"
      ]
    },
    "10": {
      "id": 10,
      "slug": "justice",
      "title": "Fairness",
      "lowDescription": "Pays little attention to whether things are just or that everyone is treated fairly.",
      "highDescription": "Has a strong sense of justice. Makes sure that everyone is treated fairly and with respect.",
      "summary": "Would prefer that people in the company are always treated fairly and with respect; considers it important that individual weaknesses are taken into consideration",
      "high": [
        "Has a strong sense of justice and makes sure everyone is treated fairly and with respect"
      ],
      "low": [
        "Pays less attention to whether things are just or everyone is treated fairly"
      ],
      "howToManage": [
        "Ask: 'What does fairness mean to you?'",
        "This person probably has a high sense of justice and what is good & right in a work related context.",
        "(Sense of fairness is one of the basic needs of humans, but some people expect less fairness from work than life in general.)",
        "Pay extra focus to respectful and fair treatment of people.",
        "Watch language; are you using words 'People', 'Talents' or 'Resources' when you are talking about humans?"
      ],
      "watchOut": []
    },
    "8": {
      "id": 8,
      "slug": "collaborate",
      "title": "Cooperativeness",
      "lowDescription": "Thinks that one is responsible for their own area alone. Rarely takes time to help others.",
      "highDescription": "Takes cooperation in the team for granted. Often takes time to support others.",
      "summary": "Prefers a climate of cooperativeness; appreciates the willing support from others when this becomes necessary; would prefer that individuals put their own personal interests aside for the benefit of others",
      "high": [
        "Takes cooperation in a team for granted; often takes time to support others"
      ],
      "low": [
        "Thinks that one is responsible for own area alone; rarely takes time to help others out"
      ],
      "howToManage": [
        "Ask: 'What does good cooperation mean for you?'",
        "Foster cooperative team spirit and reciprocity.",
        "Take care that people can get help from each other when needed.",
        "Show and illustrate how the job or task helps other people (team, clients)",
        "Try to put the team interest over individual interests."
      ],
      "watchOut": []
    },
    "4": {
      "id": 4,
      "slug": "workFun",
      "title": "Fun at work",
      "lowDescription": "Believes that a relaxed working environment is not a requirement for an efficient cooperation.",
      "highDescription": "Needs a relaxed and enjoyable work environment. Believes that work should also be fun.",
      "summary": "Prefers a relaxed and humorous working atmosphere; considers it important that despite the serious nature of the tasks one is still able to have a fair share of fun and pleasure",
      "high": [
        "Needs a relaxed and enjoyable work environment. Believes that work should also be fun."
      ],
      "low": [
        "Believes that a relaxed working environment is not a requirement for an efficient cooperation."
      ],
      "howToManage": [],
      "watchOut": []
    },
    "0": {
      "id": 0,
      "slug": "professionalChallenge",
      "title": "Professional challenge",
      "lowDescription": "Prefers tasks that do not always require special expertise or constant learning.",
      "highDescription": "Likes difficult tasks that require constant learning. Needs to be able to use own expertise.",
      "summary": "Prefers jobs which challenge individual abilities; would like to be able to utilize special knowledge or skills; prefers a working environment which requires constant learning and initiative for thinking things through",
      "high": [
        "Likes difficult tasks that require constant learning; needs to be able to use own expertise"
      ],
      "low": [
        "Prefers tasks that do not require special expertise or constant learning"
      ],
      "howToManage": [
        "Ask: 'What strengths, skills and/or knowledge do you especially like to use in your job?'",
        "Provide challenging tasks and responsibilities based on strengths, skills and expertise",
        "Provide opportunities to learn in a working environment.",
        "Might prefer more complex tasks, problems and goals that require thinking things through"
      ],
      "watchOut": [
        "'This is a routine job, do not challenge or stretch yourself too much…'"
      ]
    }
  },
  "de": {
    "15": {
      "id": 15,
      "slug": "lackOfStress",
      "title": "Work-Life-Balance",
      "lowDescription": "Hält es für nachrangig, dass Aufgaben stressfrei sind. Investiert viel Zeit und Energie in die Arbeit.",
      "highDescription": "Empfindet Überlastung und Anspannung als kontraproduktiv. Benötigt eine gute Balance zwischen Beruf und Privatleben.",
      "summary": "Würde es vorziehen, dass die Tätigkeit nicht als übermäßige Belastung wahrgenommen wird; hält es für wichtig, dass die Arbeit in der verfügbaren Zeit zu bewältigen ist und keine überhöhten Anforderungen entstehen",
      "high": [
        "'Empfindet Überlastung und Anspannung als kontraproduktiv; benötigt eine gute Balance zwischen Beruf und Privatleben.'"
      ],
      "low": [
        "Hält es für nachrangiger, dass Aufgaben stressfrei sind; investiert viel Zeit und Energie in die Arbeit"
      ],
      "howToManage": [
        "Fragen Sie: 'Was bedeutet eine gute Balance zwischen Beruf und Privatleben für Sie?'",
        "Diese Person erledigt Aufgaben gerne termingerecht, daher sind realistische Fristen wichtig.",
        "Bevorzugt möglicherweise, sich auf eine Sache zu konzentrieren und sie abzuschließen, bevor die nächste Aufgabe beginnt.",
        "Eine große Aufgabenmenge und anspruchsvolle Fristen können diese Person stark belasten.",
        "Eine klare zeitliche Trennung von Privatleben und Beruf hilft dieser Person, motiviert zu bleiben."
      ],
      "watchOut": []
    },
    "2": {
      "id": 2,
      "slug": "financialReward",
      "title": "Finanzielle Belohnung",
      "lowDescription": "Bevorzugt ein nicht leistungsorientiertes Anreizsystem. Wird durch Geld nicht wirklich motiviert.",
      "highDescription": "Bevorzugt ein leistungsorientiertes Anreizsystem. Wird durch finanzielle Belohnung motiviert.",
      "summary": "Bevorzugt eine leistungsorientierte oder angemessene Vergütung; hält es für wichtig, dass Mehrarbeit finanziell honoriert wird; wird durch finanzielle Anreize motiviert",
      "high": [
        "Bevorzugt ein leistungsorientiertes Anreizsystem und wird durch finanzielle Belohnung motiviert"
      ],
      "low": [
        "Bevorzugt ein nicht leistungsorientiertes Anreizsystem und wird durch Geld nicht wirklich motiviert"
      ],
      "howToManage": [
        "Fragen Sie: 'Welche finanziellen Anreize sind für Sie motivierend?'",
        "Bieten Sie Möglichkeiten für leistungsorientierte Vergütung oder Boni",
        "Honorieren Sie Mehrarbeit finanziell oder materiell",
        "Auch künftige Verdienstmöglichkeiten durch Karriereentwicklung oder Wachstum können diese Person motivieren"
      ],
      "watchOut": [
        "'Geld ist ein Hygienefaktor…'"
      ]
    },
    "3": {
      "id": 3,
      "slug": "security",
      "title": "Arbeitsplatzsicherheit",
      "lowDescription": "Hält Arbeitsplatzsicherheit für eher unwichtig. Ist bereit, berufliche Risiken einzugehen.",
      "highDescription": "Möchte ein Arbeitsumfeld, das Sicherheit und Zuversicht für die Zukunft bietet.",
      "summary": "Hält die Sicherheit des Arbeitsplatzes für wichtig; schätzt ein Arbeitsklima, das ein gutes Maß an Zuversicht und Unbeschwertheit hinsichtlich der eigenen Zukunft bietet",
      "high": [
        "Möchte ein Arbeitsumfeld, das Sicherheit und Zuversicht für die Zukunft bietet"
      ],
      "low": [
        "Hält Arbeitsplatzsicherheit für weniger wichtig; ist bereit, mehr berufliche Risiken einzugehen."
      ],
      "howToManage": [
        "Fragen Sie: 'Was bedeutet Sicherheit im Berufsleben für Sie?'",
        "'Sicherheit zuerst.'; achten Sie darauf, dass das Arbeitsumfeld sicher ist und Karriererisiken moderat oder geringer ausfallen",
        "Schaffen Sie Zuversicht für eine sichere und stabile Zukunft. Heben Sie stabile Elemente hervor, die zutreffen.",
        "Wenn die Zukunft des Unternehmens bzw. des Teams stark unklar, unsicher oder risikoreich ist, sinkt die Motivation."
      ],
      "watchOut": [
        "'Solche sicheren und zukunftsfähigen Positionen gibt es heutzutage nicht mehr…'"
      ]
    },
    "6": {
      "id": 6,
      "slug": "harmonia",
      "title": "Harmonie",
      "lowDescription": "Hält es für wichtig, Konflikte offen anzusprechen. Legt keinen großen Wert auf Teamharmonie.",
      "highDescription": "Schätzt Harmonie und ein Gefühl der Einheit im Team. Bemüht sich, Konflikte zu vermeiden.",
      "summary": "Schätzt ein harmonisches Arbeitsklima; möchte Streit und Auseinandersetzungen untereinander vermeiden; hält ein Zusammengehörigkeitsgefühl für wichtig",
      "high": [
        "Schätzt Harmonie und ein Gefühl der Einheit im Team; bemüht sich, Konflikte zu vermeiden."
      ],
      "low": [
        "Hält es für wichtig, Konflikte offen anzusprechen; legt keinen großen Wert auf Teamharmonie"
      ],
      "howToManage": [
        "Fragen Sie: 'Was bedeutet ein harmonisches Arbeitsumfeld für Sie?'",
        "Diese Person schätzt harmonische Beziehungen und möchte nicht unnötig Unruhe stiften; konzentrieren Sie sich besonders auf Gemeinsamkeiten und Dinge, in denen man sich einig ist.",
        "Diese Person wird durch Situationen motiviert, die einen diplomatischeren Ansatz erfordern.",
        "Vermeiden Sie Konflikte mit dieser Person so weit wie möglich. Wenn das Einheitsgefühl im Team über längere Zeit nachlässt, fühlt sich diese Person unmotiviert."
      ],
      "watchOut": []
    },
    "7": {
      "id": 7,
      "slug": "transparency",
      "title": "Transparenz",
      "lowDescription": "Akzeptiert, dass Informationen auf bestimmte Personen beschränkt sind. Geht davon aus, dass Gerüchte ohnehin entstehen. ",
      "highDescription": "Hält Transparenz und offene Kommunikation für entscheidend. Möchte stets ehrlich und offen sein.",
      "summary": "Hält es für wichtig, dass Menschen offen miteinander kommunizieren und so sichergestellt ist, dass alle relevanten Informationen bei allen ankommen; mag es nicht, wenn Gerüchte entstehen können",
      "high": [
        "Hält Transparenz und offene Kommunikation für entscheidend; möchte stets ehrlich und offen sein."
      ],
      "low": [
        "Akzeptiert, dass Informationen auf bestimmte Personen beschränkt sind; Geht davon aus, dass Gerüchte ohnehin entstehen."
      ],
      "howToManage": [
        "Fragen Sie: 'Was bedeutet offene Kommunikation für Sie?'",
        "Halten Sie die Kommunikation so offen wie möglich.",
        "Unterbinden Sie Gerüchte von Anfang an und rasch.",
        "Schaffen Sie Systeme, die offene Kommunikation im Team unterstützen."
      ],
      "watchOut": []
    },
    "1": {
      "id": 1,
      "slug": "performanceRecognition",
      "title": "Leistungsanerkennung",
      "lowDescription": "Orientiert sich an eigenen Maßstäben. Empfindet kein Bedürfnis nach Wertschätzung oder Lob.",
      "highDescription": "Strebt nach Respekt und Anerkennung. Möchte, dass Erfolge gewürdigt werden.",
      "summary": "Möchte persönliche Anerkennung für besondere Leistungen erhalten; hält es für wichtig, dass erfolgreiche Handlungen gewürdigt und entsprechend wertgeschätzt werden",
      "high": [
        "Strebt nach Respekt und Anerkennung und möchte, dass Erfolge gewürdigt werden"
      ],
      "low": [
        "Orientiert sich an eigenen Maßstäben und empfindet kein Bedürfnis nach Wertschätzung oder Lob"
      ],
      "howToManage": [
        "Fragen Sie: 'Was war die beste Anerkennung, die Sie je in einer Tätigkeit erhalten haben?'",
        "Würdigen Sie Erfolge und geben Sie persönliches Lob für positive Ergebnisse und Einsatz.",
        "Stellen Sie sicher, dass die Anerkennung auch sichtbar für andere wird - bspw. durch Veröffentlichung im Intranet, offene Kommunikation im Team oder der Abteilung.",
        "Geben Sie dieser Person nach guter Leistung möglichst Sichtbarkeit und Aufmerksamkeit."
      ],
      "watchOut": [
        "'Gute Arbeit ist Belohnung genug.'"
      ]
    },
    "12": {
      "id": 12,
      "slug": "systematicity",
      "title": "Struktur",
      "lowDescription": "Braucht wenige Strukturen, Regeln oder Verfahren. Kommt mit Ambiguität gut zurecht.",
      "highDescription": "Benötigt klar definierte Prozesse, um Unsicherheiten zu vermeiden.",
      "summary": "Hält es für wichtig, dass alle stets genau wissen, was zu tun ist, und dass Unsicherheiten bezüglich Aufgaben und Arbeitsabläufen konsequent vermieden werden",
      "high": [
        "Benötigt klar definierte Prozesse, um Unsicherheiten zu vermeiden."
      ],
      "low": [
        "Braucht wenige Strukturen, Regeln oder Verfahren. Kommt mit Ambiguität gut zurecht."
      ],
      "howToManage": [],
      "watchOut": []
    },
    "13": {
      "id": 13,
      "slug": "changeRate",
      "title": "Dynamik & Veränderung",
      "lowDescription": "Bevorzugt gut geplante und schrittweise Veränderungen. Hält Beständigkeit und Stabilität für wichtig.",
      "highDescription": "Schätzt es, wenn sich Dinge in hohem Tempo verändern. Möchte, dass Verbesserungen schnell umgesetzt werden.",
      "summary": "Schätzt es, wenn sich viele Dinge im Unternehmen in hohem Tempo verändern; hält es für wichtig, dass komplizierte Abläufe oder überholte Vorgehensweisen durch geeignete Maßnahmen sehr rasch verändert werden",
      "high": [
        "Schätzt es, wenn sich Dinge in hohem Tempo verändern; möchte, dass Verbesserungen schnell umgesetzt werden"
      ],
      "low": [
        "Bevorzugt gut geplante und schrittweise Veränderungen; hält Beständigkeit und Stabilität für wichtig"
      ],
      "howToManage": [
        "Fragen Sie: 'Was bedeutet eine höhere Veränderungsgeschwindigkeit für Sie?'",
        "Diese Person bevorzugt ein höheres Tempo bei Veränderungen. Bieten Sie möglichst Gelegenheiten, diese Veränderungen umzusetzen.",
        "Diese Person schätzt es, wenn alte Verfahren und Lösungen rasch durch neue ersetzt werden.",
        "Die Anpassung an Veränderungen und neue Anforderungen im Umfeld ist dieser Person wichtig.",
        "Fragen Sie: 'Welche Dinge hinken in unserer Organisation Ihrer Meinung nach hinterher?'"
      ],
      "watchOut": []
    },
    "16": {
      "id": 16,
      "slug": "opportunitiesForInfluence",
      "title": "Einflussnahme & Gestaltung",
      "lowDescription": "Arbeitet lieber mit klaren Vorgaben. Ist gleichgültig gegenüber Autonomie oder Macht bei der Arbeit.",
      "highDescription": "Arbeitet selbstständig und eigeninitiativ. Schätzt Gestaltungsspielraum und Einfluss.",
      "summary": "Schätzt es, wenn Eigeninitiative willkommen ist und die Bereitschaft zu eigenständigen Entscheidungen gefördert wird; möchte nicht stets für alles um Erlaubnis fragen müssen",
      "high": [
        "Arbeitet selbstständig und eigeninitiativ; schätzt Gestaltungsspielraum und Einfluss."
      ],
      "low": [
        "Arbeitet lieber mit klaren Vorgaben; ist gleichgültig gegenüber Autonomie oder Macht bei der Arbeit"
      ],
      "howToManage": [
        "Fragen Sie: 'Was bedeuten Eigeninitiative und Autonomie für Sie?'",
        "Diese Person ist motiviert, selbstständig auf ein klares Ziel hinzuarbeiten, mit Rahmenbedingungen (z. B. Budget, Zeitplan, Qualitätskriterien), die mit möglichst mit wenigen Kontrollpunkten vor der Ziellinie definiert sind.",
        "Diese Person wird dadurch motiviert, Eigeninitiative zu zeigen und nach eigenem Ermessen arbeiten zu können.",
        "Die Motivation dieser Person sinkt, wenn es viele ständige Nachfragen und Berichtspflichten gibt, übermäßig präzise Anweisungen, jemand ständig über die Schulter schaut, wiederholt um Erlaubnis gebeten werden muss usw.",
        "ACHTUNG:",
        "'Nachfassen ist gut, Kontrolle ist besser. Hier sind die Anweisungen, halten Sie sich genau daran und halten Sie die Berichte jederzeit aktuell.'"
      ],
      "watchOut": []
    },
    "5": {
      "id": 5,
      "slug": "identification",
      "title": "Identifikation",
      "lowDescription": "Hinterfragt nicht wirklich den Sinn der eigenen Arbeit. Betrachtet Arbeit lediglich als notwendigen Teil des Lebens.",
      "highDescription": "Braucht das Gefühl, bei der Arbeit etwas Sinnvolles zu tun. Möchte auf die Arbeit selbst stolz sein können.",
      "summary": "Möchte sich für die Tätigkeit begeistern können und das Gefühl haben, etwas Sinnvolles zu tun; möchte auf die eigene Arbeit stolz sein und versucht daher stets, das Allerbeste zu geben",
      "high": [
        "Braucht das Gefühl, bei der Arbeit etwas Sinnvolles zu tun; möchte auf die Arbeit selbst stolz sein können."
      ],
      "low": [
        "Hinterfragt nicht wirklich den Sinn der eigenen Arbeit; betrachtet Arbeit eher als Mittel zum Zweck"
      ],
      "howToManage": [
        "Fragen Sie: 'Was interessiert, begeistert oder fasziniert Sie in Ihrer Tätigkeit wirklich?'",
        "Bieten Sie möglichst Gelegenheiten, genau in diesen Bereichen zu arbeiten.",
        "Zeigen Sie, wie die Tätigkeit im Gesamtbild sinnvoll ist und eine tatsächliche Wirkung hat.",
        "Diese Person kann schnell Motivation verlieren, wenn die Werte von Person und Organisation nicht zusammenpassen."
      ],
      "watchOut": [
        "'Das ist nur ein Job, nicht so bedeutsam…'"
      ]
    },
    "14": {
      "id": 14,
      "slug": "developmentOpportunities",
      "title": "Entwicklungsmöglichkeiten",
      "lowDescription": "Sieht nicht das Unternehmen allein in der Verantwortung für die Kompetenzentwicklung.",
      "highDescription": "Schätzt es, wenn persönliche und berufliche Entwicklung im Unternehmen aktiv gefördert werden.",
      "summary": "Hält es für wichtig, dass persönlicher und beruflicher Entwicklung ausreichend \"Spielraum\" gegeben werden, und möchte ausreichend Gelegenheiten haben, die beruflichen Fähigkeiten zu verbessern",
      "high": [
        "Schätzt es, wenn persönliche und berufliche Entwicklung im Unternehmen aktiv gefördert werden"
      ],
      "low": [
        "Sieht nicht das Unternehmen allein in der Verantwortung für die Kompetenzentwicklung"
      ],
      "howToManage": [
        "Fragen Sie: 'Was möchten Sie lernen? Was sind Ihre drei wichtigsten Lernziele?'",
        "Bieten Sie Gelegenheiten zum Lernen, Wachsen und zur beruflichen Weiterentwicklung.",
        "Verknüpfen Sie die Lernziele dieser Person mit den Leistungszielen des Teams."
      ],
      "watchOut": [
        "Zu viele Aufgaben, Projekte und Verantwortlichkeiten, die keine Gelegenheit zum Lernen und Wachsen bieten."
      ]
    },
    "11": {
      "id": 11,
      "slug": "hierarchy",
      "title": "Hierarchie",
      "lowDescription": "Bevorzugt flache hierarchische Strukturen. Hält Autoritäten für eher unwichtig.",
      "highDescription": "Glaubt an klare Führungsstrukturen. Schätzt eindeutige Zuständigkeiten in einem Unternehmen.",
      "summary": "Bevorzugt eine klare, hierarchische Verteilung der Verantwortlichkeiten im Unternehmen; hält es für wichtig, dass es im Unternehmen Autoritäten gibt, die die Richtung klar vorgeben",
      "high": [
        "Glaubt an klare Führungsstrukturen und schätzt eindeutige Zuständigkeiten in einem Unternehmen."
      ],
      "low": [
        "Bevorzugt flache hierarchische Strukturen; hält Autoritäten für weniger wichtig"
      ],
      "howToManage": [
        "Fragen Sie: 'Was bedeuten klare Verantwortlichkeiten für Sie?'",
        "Machen Sie Verantwortungsbereiche klar: Wer ist wofür zuständig?",
        "Machen Sie deutlich, wer für bestimmte Entscheidungen verantwortlich ist."
      ],
      "watchOut": []
    },
    "17": {
      "id": 17,
      "slug": "workingEnvironment",
      "title": "Arbeitsumfeld",
      "lowDescription": "Hält die Funktionalität von Arbeitsplätzen oder die Gestaltung von Büroräumen für vernachlässigbar.",
      "highDescription": "Legt Wert auf attraktive und funktionale Büroräume und Arbeitsplätze.",
      "summary": "Bevorzugt übersichtlich gestaltete Räume und Arbeitsplätze; schätzt Arbeitsplätze, die angenehm und funktional sind",
      "high": [
        "Legt Wert auf attraktive und funktionale Büroräume und Arbeitsplätze"
      ],
      "low": [
        "Hält die Funktionalität von Arbeitsplätzen oder die Gestaltung von Büroräumen für vernachlässigbar"
      ],
      "howToManage": [
        "Fragen Sie: 'Was bedeutet ein funktionaler und attraktiver Arbeitsplatz für Sie?'",
        "Sorgen Sie möglichst für ein angenehmes Arbeitsumfeld; für verschiedene Menschen kann das Ästhetik, Funktionalität, Sauberkeit oder Sicherheit bedeuten. Finden Sie heraus, was diese Person am meisten schätzt, und nehmen Sie relevante Änderungen vor, wann immer möglich.",
        "Geben Sie dieser Person möglichst Gelegenheit, den Arbeitsplatz passender zu gestalten; unterschiedliche Dekoration, Anordnung und Lage können motivierend sein."
      ],
      "watchOut": []
    },
    "9": {
      "id": 9,
      "slug": "unity",
      "title": "Integrität",
      "lowDescription": "Hält Ethik und Moral im Arbeitskontext für unerheblich. Muss nicht unbedingt hinter bestimmten Zielen stehen, um sie zu vertreten.",
      "highDescription": "Hält moralische Grundsätze für wichtig und möchte mit gutem Gewissen arbeiten können.",
      "summary": "Hält es für wichtig, dass Ziele verfolgt werden, hinter denen man mit Überzeugung stehen kann, und klare ethische Grundsätze eingehalten werden; schätzt ein vertrauensvolles Miteinander",
      "high": [
        "Hält moralische Grundsätze für wichtig und möchte mit gutem Gewissen arbeiten können."
      ],
      "low": [
        "Hält Ethik und Moral im Arbeitskontext für eher unerheblich; Muss nicht unbedingt hinter bestimmten Zielen stehen, um sie zu vertreten."
      ],
      "howToManage": [
        "Fragen Sie: 'Welche ethischen/moralischen Grundsätze sind Ihnen besonders wichtig?'",
        "Diese Person hat vermutlich einige sehr bedeutsame ethische Werte, die im Arbeitsalltag gelebt werden müssen.",
        "Betonen Sie größtmögliche Transparenz und Ehrlichkeit in Ihrem Handeln und Ihren Entscheidungen"
      ],
      "watchOut": [
        "'Nun, das ist nicht ganz richtig, aber es ist nicht gesetzeswidrig.'"
      ]
    },
    "10": {
      "id": 10,
      "slug": "justice",
      "title": "Gerechtigkeit",
      "lowDescription": "Achtet wenig darauf, ob Dinge gerecht sind oder alle fair behandelt werden.",
      "highDescription": "Hat ein starkes Gerechtigkeitsempfinden. Achtet darauf, dass alle fair und respektvoll behandelt werden.",
      "summary": "Würde es vorziehen, dass Menschen im Unternehmen stets fair und respektvoll behandelt werden; hält es für wichtig, dass individuelle Schwächen berücksichtigt werden",
      "high": [
        "Hat ein starkes Gerechtigkeitsempfinden und achtet darauf, dass alle fair und respektvoll behandelt werden"
      ],
      "low": [
        "Achtet weniger darauf, ob Dinge gerecht sind oder alle fair behandelt werden"
      ],
      "howToManage": [
        "Fragen Sie: 'Was bedeutet Gerechtigkeit für Sie?'",
        "Diese Person hat vermutlich ein ausgeprägtes Gerechtigkeitsempfinden und eine klare Vorstellung davon, was im Arbeitskontext gut und richtig ist.",
        "(Gerechtigkeitsempfinden ist ein Grundbedürfnis des Menschen, aber manche Menschen erwarten von der Arbeit weniger Gerechtigkeit als vom Leben insgesamt.)",
        "Legen Sie besonderen Wert auf respektvolle und faire Behandlung von Menschen.",
        "Achten Sie auf die Sprache: Verwenden Sie die Wörter 'Menschen', 'Talente' oder 'Ressourcen', wenn Sie über Mitarbeitende sprechen?"
      ],
      "watchOut": []
    },
    "8": {
      "id": 8,
      "slug": "collaborate",
      "title": "Kooperationsbereitschaft",
      "lowDescription": "Denkt, dass man allein für den eigenen Bereich verantwortlich ist. Nimmt sich selten Zeit, anderen zu helfen.",
      "highDescription": "Betrachtet Zusammenarbeit im Team als selbstverständlich. Nimmt sich oft Zeit, andere zu unterstützen.",
      "summary": "Bevorzugt ein Klima der Kooperationsbereitschaft; schätzt die bereitwillige Unterstützung durch andere, wenn diese nötig wird; würde es vorziehen, dass Einzelne eigene Interessen zugunsten anderer zurückstellen",
      "high": [
        "Betrachtet Zusammenarbeit im Team als selbstverständlich; nimmt sich oft Zeit, andere zu unterstützen"
      ],
      "low": [
        "Denkt, dass man allein für den eigenen Bereich verantwortlich ist; nimmt sich selten Zeit, anderen zu helfen"
      ],
      "howToManage": [
        "Fragen Sie: 'Was bedeutet gute Zusammenarbeit für Sie?'",
        "Fördern Sie kooperativen Teamgeist und Gegenseitigkeit.",
        "Sorgen Sie dafür, dass Menschen sich bei Bedarf gegenseitig helfen können.",
        "Zeigen und verdeutlichen Sie, wie die Tätigkeit oder Aufgabe anderen Menschen hilft (Team, Kunden)",
        "Versuchen Sie, das Teaminteresse über individuelle Interessen zu stellen."
      ],
      "watchOut": []
    },
    "4": {
      "id": 4,
      "slug": "workFun",
      "title": "Spaß an der Arbeit",
      "lowDescription": "Ist der Auffassung, dass ein entspanntes Arbeitsumfeld keine Voraussetzung für eine effiziente Zusammenarbeit ist.",
      "highDescription": "Braucht ein entspanntes und angenehmes Arbeitsumfeld. Ist der Auffassung, dass Arbeit auch Spaß machen sollte.",
      "summary": "Bevorzugt ein entspanntes und humorvolles Arbeitsklima; hält es für wichtig, dass trotz des wichtigen Charakters der Aufgaben dennoch ein angemessener Anteil an Spaß und Freude möglich ist",
      "high": [
        "Braucht ein entspanntes und angenehmes Arbeitsumfeld. Ist der Auffassung, dass Arbeit auch Spaß machen sollte."
      ],
      "low": [
        "Ist der Auffassung, dass ein entspanntes Arbeitsumfeld keine Voraussetzung für eine effiziente Zusammenarbeit ist."
      ],
      "howToManage": [],
      "watchOut": []
    },
    "0": {
      "id": 0,
      "slug": "professionalChallenge",
      "title": "Berufliche Herausforderung",
      "lowDescription": "Bevorzugt Aufgaben, die nicht immer besondere Fachkenntnisse oder kontinuierliches Lernen erfordern.",
      "highDescription": "Mag schwierige Aufgaben, die kontinuierliches Lernen erfordern. Muss das eigene Fachwissen einsetzen können.",
      "summary": "Bevorzugt Tätigkeiten, die die eigenen Fähigkeiten herausfordern; möchte besonderes Wissen oder besondere Fähigkeiten einsetzen können; bevorzugt ein Arbeitsumfeld, das kontinuierliches Lernen und die Initiative zum Durchdenken von Sachverhalten erfordert",
      "high": [
        "Mag schwierige Aufgaben, die kontinuierliches Lernen erfordern; muss das eigene Fachwissen einsetzen können"
      ],
      "low": [
        "Bevorzugt Aufgaben, die keine besonderen Fachkenntnisse oder kontinuierliches Lernen erfordern"
      ],
      "howToManage": [
        "Fragen Sie: 'Welche Stärken, Fähigkeiten und/oder Kenntnisse setzen Sie in Ihrer Tätigkeit besonders gerne ein?'",
        "Bieten Sie anspruchsvolle Aufgaben und Verantwortlichkeiten auf Basis von Stärken, Fähigkeiten und Fachwissen",
        "Bieten Sie Gelegenheiten zum Lernen im Arbeitsumfeld.",
        "Bevorzugt möglicherweise komplexere Aufgaben, Probleme und Ziele, die Nachdenken erfordern"
      ],
      "watchOut": [
        "'Das ist eine Routineaufgabe, fordern und strecken Sie sich nicht zu sehr…'"
      ]
    }
  },
  "fi": {
    "0": {
      "id": 0,
      "slug": "professionalChallenge",
      "title": "Ammatillinen haaste",
      "lowDescription": "Pitää tehtävistä, jotka eivät aina vaadi erityisosaamista tai jatkuvaa oppimista.",
      "highDescription": "Pitää vaikeista tehtävistä, jotka vaativat jatkuvaa oppimista. Tarvitsee mahdollisuuden käyttää omaa asiantuntemustaan.",
      "summary": "Suosii tehtäviä, jotka haastavat omia kykyjä; haluaisi voida hyödyntää erityisosaamistaan tai -taitojaan; suosii työympäristöä, joka vaatii jatkuvaa oppimista ja omatoimista asioiden pohtimista",
      "high": [
        "Pitää vaikeista tehtävistä, jotka vaativat jatkuvaa oppimista; tarvitsee mahdollisuuden käyttää omaa asiantuntemustaan"
      ],
      "low": [
        "Pitää tehtävistä, jotka eivät vaadi erityisosaamista tai jatkuvaa oppimista"
      ],
      "howToManage": [
        "Kysy: 'Mitä vahvuuksia, taitoja ja/tai tietoja haluat erityisesti käyttää työssäsi?'",
        "Tarjoa haastavia tehtäviä ja vastuuta vahvuuksien, taitojen ja asiantuntemuksen perusteella",
        "Tarjoa mahdollisuuksia oppia työn ohessa.",
        "Saattaa pitää enemmän monimutkaisista tehtävistä, ongelmista ja tavoitteista, jotka vaativat asioiden huolellista pohtimista"
      ],
      "watchOut": [
        "'Tämä on rutiinityö, älä haasta tai venytä itseäsi liikaa…'"
      ]
    },
    "1": {
      "id": 1,
      "slug": "performanceRecognition",
      "title": "Suorituksen tunnustaminen",
      "lowDescription": "Luottaa omiin mittapuihinsa. Ei koe tarvetta saada arvostusta tai kiitosta.",
      "highDescription": "Tavoittelee kunnioitusta ja tunnustusta. Haluaa, että onnistumiset huomioidaan.",
      "summary": "Haluaisi saada henkilökohtaista tunnustusta erityisistä saavutuksista; pitää tärkeänä, että onnistumiset huomataan ja niitä myös arvostetaan ansaitusti",
      "high": [
        "Tavoittelee kunnioitusta ja tunnustusta ja haluaa, että onnistumiset huomioidaan"
      ],
      "low": [
        "Luottaa omiin mittapuihinsa eikä koe tarvetta saada arvostusta tai kiitosta"
      ],
      "howToManage": [
        "Kysy: 'Mikä on paras tunnustus, jonka olet koskaan saanut työssäsi?'",
        "Huomioi onnistumiset ja kiitä henkilökohtaisesti hyvistä tuloksista ja vaivannäöstä.",
        "Varmista, että myönteinen palaute ja tunnustus näkyvät myös muille – esim. julkaisemalla ne intranetissä tai viestimällä niistä avoimesti tiimissä tai osastolla.",
        "Jos mahdollista, nosta tämä henkilö esiin, kun hän on tehnyt hyvää työtä."
      ],
      "watchOut": [
        "'Hyvä työ on palkinto itsessään.'"
      ]
    },
    "2": {
      "id": 2,
      "slug": "financialReward",
      "title": "Taloudellinen palkkio",
      "lowDescription": "Suosii kannustinjärjestelmää, joka ei perustu suoritukseen. Raha ei juuri motivoi häntä.",
      "highDescription": "Suosii suoritusperusteista kannustinjärjestelmää. Taloudellinen palkkio motivoi häntä.",
      "summary": "Suosii suoritusperusteista tai riittävää palkkaa; katsoo, että ylimääräisestä työstä tulee saada taloudellinen korvaus; taloudelliset kannustimet motivoivat häntä",
      "high": [
        "Suosii suoritusperusteista kannustinjärjestelmää, ja taloudellinen palkkio motivoi häntä"
      ],
      "low": [
        "Suosii kannustinjärjestelmää, joka ei perustu suoritukseen, eikä raha juuri motivoi häntä"
      ],
      "howToManage": [
        "Kysy: 'Millaiset taloudelliset palkkiot ovat sinulle motivoivia?'",
        "Tarjoa mahdollisuuksia suoritusperusteiseen palkkaan tai bonuksiin",
        "Palkitse ylimääräinen työ rahallisesti tai aineellisesti",
        "Tulevaisuuden ansaintamahdollisuudet urakehityksen tai kasvun kautta saattavat myös motivoida tätä henkilöä"
      ],
      "watchOut": [
        "'Raha on hygieniatekijä…'"
      ]
    },
    "3": {
      "id": 3,
      "slug": "security",
      "title": "Työpaikan pysyvyys",
      "lowDescription": "Pitää työpaikan pysyvyyttä melko yhdentekevänä. On valmis ottamaan ammatillisia riskejä.",
      "highDescription": "Haluaa työympäristön, joka luo turvallisuutta ja luottamusta tulevaisuuteen.",
      "summary": "Pitää työpaikan pysyvyyttä tärkeänä; arvostaa työilmapiiriä, jossa voi tuntea luottamusta omaan tulevaisuuteensa ja olla siitä huoleton",
      "high": [
        "Haluaa työympäristön, joka luo turvallisuutta ja luottamusta tulevaisuuteen"
      ],
      "low": [
        "Pitää työpaikan pysyvyyttä vähemmän tärkeänä; on valmis ottamaan enemmän ammatillisia riskejä."
      ],
      "howToManage": [
        "Kysy: 'Mitä turvallisuus työelämässä merkitsee sinulle?'",
        "'Turvallisuus ensin.'; huolehdi, että työympäristö on turvallinen ja urariskit ovat enintään kohtuullisia",
        "Rakenna luottamusta turvalliseen ja vakaaseen tulevaisuuteen. Korosta niitä vakauden tekijöitä, jotka pitävät paikkansa.",
        "Jos yrityksen / tiimin tulevaisuus on erittäin epävarma, epämääräinen tai riskialtis, motivaatio laskee."
      ],
      "watchOut": [
        "'Nykyään ei ole olemassa täysin varmoja ja tulevaisuudenkestäviä työpaikkoja…'"
      ]
    },
    "4": {
      "id": 4,
      "slug": "workFun",
      "title": "Työn hauskuus",
      "lowDescription": "Ei pidä rentoa työympäristöä tehokkaan yhteistyön edellytyksenä.",
      "highDescription": "Tarvitsee rentoa ja miellyttävää työympäristöä. Uskoo, että työn pitäisi olla myös hauskaa.",
      "summary": "Suosii rentoa ja huumorintäyteistä työilmapiiriä; pitää tärkeänä, että tehtävien vakavuudesta huolimatta työssä voi myös pitää hauskaa",
      "high": [
        "Tarvitsee rentoa ja miellyttävää työympäristöä. Uskoo, että työn pitäisi olla myös hauskaa."
      ],
      "low": [
        "Ei pidä rentoa työympäristöä tehokkaan yhteistyön edellytyksenä."
      ],
      "howToManage": [],
      "watchOut": []
    },
    "5": {
      "id": 5,
      "slug": "identification",
      "title": "Työhön samaistuminen",
      "lowDescription": "Ei oikeastaan kyseenalaista oman työn merkitystä. Pitää työtä vain elämän välttämättömänä osana.",
      "highDescription": "Tarvitsee tunteen siitä, että tekee työssään jotain merkityksellistä. Haluaa olla ylpeä itse työstä.",
      "summary": "Haluaisi voida innostua työstään ja tuntea tekevänsä jotain merkityksellistä; haluaa olla ylpeä omasta työstään ja yrittää siksi aina antaa parhaansa",
      "high": [
        "Tarvitsee tunteen siitä, että tekee työssään jotain merkityksellistä; haluaa olla ylpeä itse työstä."
      ],
      "low": [
        "Ei oikeastaan kyseenalaista oman työn merkitystä; pitää työtä enemmän keinona päämäärän saavuttamiseen"
      ],
      "howToManage": [
        "Kysy: 'Mikä työssäsi todella kiinnostaa, innostaa tai jopa sytyttää sinua?'",
        "Tarjoa mahdollisuuksia työskennellä erityisesti näillä alueilla, kun se on mahdollista.",
        "Näytä, miten työ on merkityksellistä isossa kuvassa ja miten sillä on pysyvämpi vaikutus.",
        "Tämä henkilö saattaa menettää motivaationsa nopeasti, jos hänen ja organisaation arvot eivät kohtaa."
      ],
      "watchOut": [
        "'Tämä on vain työ, ei niin merkityksellistä…'"
      ]
    },
    "6": {
      "id": 6,
      "slug": "harmonia",
      "title": "Harmonia",
      "lowDescription": "Pitää tärkeänä käsitellä konflikteja avoimesti. Ei pidä tiimin harmoniaa kovin tärkeänä.",
      "highDescription": "Arvostaa harmoniaa ja yhteenkuuluvuuden tunnetta tiimissä. Pyrkii välttämään konflikteja.",
      "summary": "Arvostaa harmonista työilmapiiriä; haluaisi välttää keskinäisiä riitoja ja kiistoja; pitää yhteenkuuluvuuden tunnetta tärkeänä",
      "high": [
        "Arvostaa harmoniaa ja yhteenkuuluvuuden tunnetta tiimissä; pyrkii välttämään konflikteja."
      ],
      "low": [
        "Pitää tärkeänä käsitellä konflikteja avoimesti; ei pidä tiimin harmoniaa kovin tärkeänä"
      ],
      "howToManage": [
        "Kysy: 'Mitä harmoninen työympäristö merkitsee sinulle?'",
        "Tämä henkilö arvostaa harmonisia suhteita eikä halua 'keikuttaa venettä'; keskity erityisesti yhteisiin näkemyksiin ja asioihin, joista ihmiset ovat samaa mieltä.",
        "Tätä henkilöä motivoivat tilanteet, jotka vaativat diplomaattisempaa lähestymistapaa.",
        "Vältä konflikteja tämän henkilön kanssa mahdollisimman paljon. Jos tiimin yhteenkuuluvuuden tunne on pitkään heikko, tämän henkilön motivaatio alkaa hiipua."
      ],
      "watchOut": []
    },
    "7": {
      "id": 7,
      "slug": "transparency",
      "title": "Eettisyys",
      "lowDescription": "Hyväksyy, että osa tiedosta on vain tiettyjen henkilöiden saatavilla. Ottaa huhut huomioon.",
      "highDescription": "Pitää läpinäkyvyyttä ja avointa viestintää ratkaisevan tärkeinä. Haluaa aina olla rehellinen ja suora.",
      "summary": "Pitää tärkeänä, että ihmiset viestivät keskenään avoimesti, jotta kaikki saavat kaiken oleellisen tiedon; ei pidä siitä, että huhuja pääsee syntymään",
      "high": [
        "Pitää läpinäkyvyyttä ja avointa viestintää ratkaisevan tärkeinä; haluaa aina olla rehellinen ja suora."
      ],
      "low": [
        "Hyväksyy, että osa tiedosta on vain tiettyjen henkilöiden saatavilla; ottaa huhut huomioon."
      ],
      "howToManage": [
        "Kysy: 'Mitä avoin viestintä merkitsee sinulle?'",
        "Pidä viestintä mahdollisimman avoimena.",
        "Tukahduta huhut nopeasti heti alkuunsa.",
        "Luo toimintatapoja, jotka tukevat tiimin avointa viestintää."
      ],
      "watchOut": []
    },
    "8": {
      "id": 8,
      "slug": "collaborate",
      "title": "Yhteistyöhenkisyys",
      "lowDescription": "Ajattelee olevansa vastuussa vain omasta alueestaan. Käyttää harvoin aikaa muiden auttamiseen.",
      "highDescription": "Pitää tiimin yhteistyötä itsestäänselvyytenä. Käyttää usein aikaa muiden tukemiseen.",
      "summary": "Suosii yhteistyöhenkistä ilmapiiriä; arvostaa muiden auliisti antamaa tukea, kun sitä tarvitaan; toivoisi, että yksilöt jättäisivät omat etunsa sivuun muiden hyväksi",
      "high": [
        "Pitää tiimin yhteistyötä itsestäänselvyytenä; käyttää usein aikaa muiden tukemiseen"
      ],
      "low": [
        "Ajattelee olevansa vastuussa vain omasta alueestaan; käyttää harvoin aikaa muiden auttamiseen"
      ],
      "howToManage": [
        "Kysy: 'Mitä hyvä yhteistyö tarkoittaa sinulle?'",
        "Edistä yhteistyöhön perustuvaa tiimihenkeä ja vastavuoroisuutta.",
        "Varmista, että ihmiset voivat saada apua toisiltaan tarvittaessa.",
        "Näytä ja havainnollista, kuinka työ tai tehtävä auttaa muita ihmisiä (tiimiä, asiakkaita)",
        "Yritä asettaa tiimin etu yksilöetujen edelle."
      ],
      "watchOut": []
    },
    "9": {
      "id": 9,
      "slug": "unity",
      "title": "Eettisyys",
      "lowDescription": "Pitää eettisiä ja moraalisia kysymyksiä työympäristössä merkityksettöminä. Ei koe tarvetta seistä tavoitteiden takana.",
      "highDescription": "Pitää moraalisia periaatteita tärkeinä ja haluaa pystyä työskentelemään puhtaalla omallatunnolla.",
      "summary": "Pitää tärkeänä, että tavoitellaan päämääriä, joiden takana voi seistä vakaumuksella, ja että selkeitä eettisiä periaatteita noudatetaan; arvostaa keskinäistä luottamusta",
      "high": [
        "Pitää moraalisia periaatteita tärkeinä ja haluaa pystyä työskentelemään puhtaalla omallatunnolla."
      ],
      "low": [
        "Pitää eettisiä ja moraalisia kysymyksiä työympäristössä merkityksettöminä; ei tarvitse seistä tavoitteiden takana."
      ],
      "howToManage": [
        "Kysy: 'Mitkä eettiset periaatteet ovat sinulle erityisen tärkeitä?'",
        "Tällä henkilöllä on todennäköisesti joitakin hyvin tärkeitä eettisiä arvoja, joiden täytyy toteutua päivittäisessä työssä.",
        "Pyri mahdollisimman suureen läpinäkyvyyteen ja rehellisyyteen toiminnassasi ja päätöksissäsi"
      ],
      "watchOut": [
        "'No, ei tämä ihan oikein ole, mutta ei se laitontakaan ole.'"
      ]
    },
    "10": {
      "id": 10,
      "slug": "justice",
      "title": "Oikeudenmukaisuus",
      "lowDescription": "Kiinnittää vähän huomiota siihen, ovatko asiat oikeudenmukaisia tai kohdellaanko kaikkia tasapuolisesti.",
      "highDescription": "Hänellä on vahva oikeudentaju. Hän varmistaa, että kaikkia kohdellaan oikeudenmukaisesti ja kunnioittavasti.",
      "summary": "Haluaisi, että yrityksessä kohdeltaisiin ihmisiä aina oikeudenmukaisesti ja kunnioittavasti; pitää tärkeänä, että yksilölliset heikkoudet otetaan huomioon",
      "high": [
        "Hänellä on vahva oikeudentaju, ja hän varmistaa, että kaikkia kohdellaan oikeudenmukaisesti ja kunnioittavasti"
      ],
      "low": [
        "Kiinnittää vähemmän huomiota siihen, ovatko asiat oikeudenmukaisia tai kohdellaanko kaikkia tasapuolisesti"
      ],
      "howToManage": [
        "Kysy: 'Mitä oikeudenmukaisuus merkitsee sinulle?'",
        "Tällä henkilöllä on todennäköisesti vahva oikeudentaju ja käsitys siitä, mikä on työelämässä hyvää ja oikein.",
        "(Oikeudenmukaisuuden tunne on yksi ihmisen perustarpeista, mutta jotkut odottavat työltä vähemmän oikeudenmukaisuutta kuin elämältä yleensä.)",
        "Kiinnitä erityistä huomiota ihmisten kunnioittavaan ja reiluun kohteluun.",
        "Kiinnitä huomiota kielenkäyttöön; käytätkö sanoja 'ihmiset', 'osaajat' vai 'resurssit', kun puhut ihmisistä?"
      ],
      "watchOut": []
    },
    "11": {
      "id": 11,
      "slug": "hierarchy",
      "title": "Hierarkia",
      "lowDescription": "Suosii matalia hierarkioita. Pitää auktoriteetteja melko merkityksettöminä.",
      "highDescription": "Uskoo selkeisiin johtamisrakenteisiin. Arvostaa selkeitä auktoriteetteja yrityksessä.",
      "summary": "Pitää selkeästä, hierarkkisesta vastuunjaosta yrityksessä; pitää tärkeänä, että yrityksessä on auktoriteetteja, jotka linjaavat toimintatavat selkeästi",
      "high": [
        "Uskoo selkeisiin johtamisrakenteisiin ja arvostaa selkeitä auktoriteetteja yrityksessä."
      ],
      "low": [
        "Suosii matalia hierarkioita; pitää auktoriteetteja vähemmän tärkeinä"
      ],
      "howToManage": [
        "Kysy: 'Mitä selkeät vastuut tarkoittavat sinulle?'",
        "Tee vastuualueet selviksi: kuka on vastuussa mistäkin?",
        "Tee selväksi, kuka on vastuussa tietyistä päätöksistä."
      ],
      "watchOut": []
    },
    "12": {
      "id": 12,
      "slug": "systematicity",
      "title": "Rakenne",
      "lowDescription": "Tarvitsee vähän rakenteita, sääntöjä tai menettelyjä. Sietää epäselvyyksiä hyvin.",
      "highDescription": "Vaatii tarkasti määriteltyjä prosesseja epävarmuuksien välttämiseksi.",
      "summary": "Pitää tärkeänä, että kaikki tietävät aina tarkalleen, mitä pitää tehdä, ja että työtehtäviin ja työvaiheiden järjestykseen liittyvää epäselvyyttä vältetään tarkasti",
      "high": [
        "Vaatii tarkasti määriteltyjä prosesseja epävarmuuksien välttämiseksi."
      ],
      "low": [
        "Tarvitsee vähän rakenteita, sääntöjä tai menettelyjä. Sietää epäselvyyksiä hyvin."
      ],
      "howToManage": [],
      "watchOut": []
    },
    "13": {
      "id": 13,
      "slug": "changeRate",
      "title": "Muutostahti",
      "lowDescription": "Suosii hyvin suunniteltuja ja asteittaisia muutoksia. Pitää johdonmukaisuutta ja vakautta tärkeänä.",
      "highDescription": "Arvostaa, kun asiat muuttuvat nopeasti. Haluaa, että parannukset toteutetaan nopeasti.",
      "summary": "Arvostaa sitä, että yrityksessä muuttuu moni asia nopeasti; pitää tärkeänä, että monimutkaiset toimintatavat tai vanhentuneet käytännöt uudistetaan hyvin nopeasti tarkoituksenmukaisin toimenpitein",
      "high": [
        "Arvostaa, kun asiat muuttuvat nopeasti; haluaa parannusten toteutuvan nopeasti"
      ],
      "low": [
        "Suosii hyvin suunniteltuja ja asteittaisia muutoksia; pitää johdonmukaisuutta ja vakautta tärkeänä"
      ],
      "howToManage": [
        "Kysy: 'Mitä nopeampi muutostahti merkitsee sinulle?'",
        "Tämä henkilö toivoo muutosten etenevän nopeammin. Anna hänelle mahdollisuuksia toteuttaa muutoksia aina kun voit.",
        "Tämä henkilö arvostaa, että vanhat menettelytavat ja ratkaisut korvataan nopeasti uusilla.",
        "Tälle henkilölle on tärkeää sopeutua ympäristön muutoksiin ja uusiin vaatimuksiin.",
        "Kysy: 'Missä asioissa organisaatiomme on mielestäsi jäljessä?'"
      ],
      "watchOut": []
    },
    "14": {
      "id": 14,
      "slug": "developmentOpportunities",
      "title": "Kehitysmahdollisuudet",
      "lowDescription": "Ei koe, että yritys yksin olisi vastuussa taitojen kehittämisestä.",
      "highDescription": "Arvostaa, kun yrityksessä edistetään aktiivisesti henkilökohtaista kehitystä ja urakehitystä.",
      "summary": "Pitää tärkeänä, että henkilökohtaiselle kehitykselle ja urakehitykselle annetaan riittävästi tilaa, ja haluaisi saada riittävästi mahdollisuuksia kehittää ammatillisia taitojaan",
      "high": [
        "Arvostaa sitä, kun yrityksessä edistetään aktiivisesti henkilökohtaista kehitystä ja urakehitystä"
      ],
      "low": [
        "Ei koe, että yritys yksin olisi vastuussa taitojen kehittämisestä"
      ],
      "howToManage": [
        "Kysy: 'Mitä haluat oppia? Mitkä ovat kolme tärkeintä oppimistavoitettasi?'",
        "Tarjoa mahdollisuuksia oppimiseen, kasvuun ja ammatilliseen kehittymiseen.",
        "Pyri yhdistämään tämän henkilön oppimistavoitteet tiimin suoritustavoitteisiin."
      ],
      "watchOut": [
        "Liian monta tehtävää, projektia ja vastuuta, jotka eivät tarjoa mahdollisuuksia oppia ja kasvaa."
      ]
    },
    "15": {
      "id": 15,
      "slug": "lackOfStress",
      "title": "Työn ja vapaa-ajan tasapaino",
      "lowDescription": "Ei pidä tärkeänä, että tehtävät ovat stressittömiä. Käyttää paljon aikaa ja energiaa työhönsä.",
      "highDescription": "Kokee ylikuormituksen ja jännitteet tuloksellisuutta heikentävinä. Tarvitsee hyvän työn ja vapaa-ajan tasapainon.",
      "summary": "Toivoo, ettei työ aiheuta liiallista stressiä; pitää tärkeänä, että työ ehditään tehdä käytettävissä olevassa ajassa eikä liiallisia vaatimuksia ilmene",
      "high": [
        "'Kokee ylikuormituksen ja jännitteet tuloksellisuutta heikentävinä; tarvitsee hyvän työn ja vapaa-ajan tasapainon.'"
      ],
      "low": [
        "Pitää vähemmän tärkeänä, että tehtävät ovat stressittömiä; panostaa työhön paljon aikaa ja energiaa"
      ],
      "howToManage": [
        "Kysy: 'Mitä hyvä työn ja vapaa-ajan tasapaino merkitsee sinulle?'",
        "Tämä henkilö haluaa saada asiat valmiiksi ajallaan, joten realistiset määräajat ovat hänelle tärkeitä.",
        "Saattaa haluta keskittyä yhteen asiaan kerrallaan ja saada sen valmiiksi ennen seuraavaan tehtävään siirtymistä.",
        "Suuri määrä tehtäviä ja haastavat määräajat voivat aiheuttaa tälle henkilölle paljon stressiä.",
        "Työn ja muun elämän selkeä erottaminen toisistaan ajankäytössä auttaa tätä henkilöä pysymään motivoituneena."
      ],
      "watchOut": []
    },
    "16": {
      "id": 16,
      "slug": "opportunitiesForInfluence",
      "title": "Vaikutusmahdollisuudet",
      "lowDescription": "Työskentelee mieluiten selkeiden ohjeiden mukaan. Ei pidä itsenäisyyttä tai valtaa työssä tärkeänä.",
      "highDescription": "Työskentelee itsenäisesti ja oma-aloitteisesti. Pitää siitä, että saa luovaa liikkumavaraa ja vaikutusmahdollisuuksia.",
      "summary": "Arvostaa, kun oma-aloitteisuuteen suhtaudutaan myönteisesti ja halua tehdä omia päätöksiä tuetaan; ei haluaisi joutua aina kysymään lupaa kaikkeen",
      "high": [
        "Työskentelee itsenäisesti ja oma-aloitteisesti; pitää siitä, että saa luovaa liikkumavaraa ja vaikutusmahdollisuuksia."
      ],
      "low": [
        "Työskentelee mieluiten selkeiden ohjeiden mukaan; ei pidä itsenäisyyttä tai valtaa työssä tärkeänä"
      ],
      "howToManage": [
        "Kysy: 'Mitä oma-aloitteisuus ja itsenäisyys merkitsevät sinulle?'",
        "Tämä henkilö motivoituu, kun hän saa työskennellä itsenäisemmin kohti selkeää tavoitetta niin, että rajaehdot (esim. budjetti, aikataulu, laatukriteerit) on määritelty ja ennen maalia on mahdollisuuksien mukaan vain yksi välitarkistus.",
        "Tätä henkilöä motivoi, kun häntä rohkaistaan oma-aloitteisuuteen ja hän saa toimia oman harkintansa mukaan.",
        "Tämän henkilön motivaatio laskee, kun työhön liittyy jatkuvaa seurantaa ja raportointia, liian tarkkoja ohjeita, joku valvoo jatkuvasti olan takana tai lupia täytyy pyytää yhä uudelleen jne.",
        "VARO:",
        "'Seuranta on hyvä, valvonta vielä parempi. Tässä ovat ohjeet, noudata niitä tarkasti ja pidä raportointi koko ajan ajan tasalla.'"
      ],
      "watchOut": []
    },
    "17": {
      "id": 17,
      "slug": "workingEnvironment",
      "title": "Työympäristö",
      "lowDescription": "Pitää työpisteiden toimivuutta tai toimitilojen suunnittelua vähäpätöisenä.",
      "highDescription": "Arvostaa viihtyisiä ja toimivia toimitiloja ja työpisteitä.",
      "summary": "Pitää hyvin järjestetyistä tiloista ja työpisteistä; arvostaa miellyttäviä ja toimivia työpisteitä",
      "high": [
        "Arvostaa viihtyisiä ja toimivia toimitiloja ja työpisteitä"
      ],
      "low": [
        "Pitää työpisteiden toimivuutta tai toimitilojen suunnittelua vähäpätöisenä"
      ],
      "howToManage": [
        "Kysy: 'Mitä toimiva ja viihtyisä työympäristö merkitsee sinulle?'",
        "Tarjoa mahdollisimman miellyttävä työympäristö; eri ihmisille tämä voi tarkoittaa estetiikkaa, toimivuutta, siisteyttä tai turvallisuutta. Selvitä, mitä tämä henkilö arvostaa eniten, ja tee tarvittavia muutoksia aina kun voit.",
        "Anna tälle henkilölle mahdollisuuksien mukaan tilaisuus muokata työpistettään itselleen sopivammaksi; erilaiset koristeet, järjestelyt ja sijainti voivat motivoida häntä."
      ],
      "watchOut": []
    }
  }
};

export default dictionary;
